import { useState, useEffect, useRef } from "react";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { api } from "../lib/api";

export const ENGAGEMENTS = [
  { v: "independent_contractor", l: "Independent Contractor" },
  { v: "safa", l: "Services & Facilities Agreement (SAFA)" },
  { v: "employee_commission", l: "Employee — Commission/Retainer" },
  { v: "employee_salary", l: "Employee — Salary" },
  { v: "casual", l: "Casual Employee" },
  { v: "other", l: "Other" },
  { v: "unsure", l: "Unsure" },
];
export const METHODS = [
  { v: "adjusted_billing", l: "Adjusted billing (deductions + service fee)" },
  { v: "percentage_billings", l: "Percentage of billings" },
  { v: "percentage_collections", l: "Percentage of collections" },
  { v: "percentage_after_lab", l: "Percentage after lab fees" },
  { v: "hourly", l: "Hourly" },
  { v: "daily", l: "Daily rate" },
  { v: "fixed", l: "Fixed amount" },
  { v: "custom", l: "Custom arrangement" },
];
export const LAB = [
  { v: "practice_pays", l: "Practice pays all lab fees" },
  { v: "dentist_pays", l: "Dentist pays all lab fees" },
  { v: "shared", l: "Lab fees shared" },
];
export const FREQS = [
  { v: "weekly", l: "Weekly" }, { v: "fortnightly", l: "Fortnightly" },
  { v: "monthly", l: "Monthly" }, { v: "irregular", l: "Irregular" }, { v: "other", l: "Custom / other" },
];
export const SUPERS = [
  { v: "practice_separate", l: "Practice contributes separately" },
  { v: "dentist_manages", l: "Dentist manages own contributions" },
  { v: "both", l: "Both" },
  { v: "none", l: "None under current recorded arrangement" },
  { v: "unsure", l: "Unsure / review required" },
];
export const GSTS = [
  { v: "configured_accountant", l: "Configured with accountant" },
  { v: "gst_registered", l: "GST registered" },
  { v: "not_registered", l: "Not GST registered" },
  { v: "review_required", l: "Review required" },
];
export const FEE_BASIS = [
  { v: "practice_share", l: "Practice keeps the fee — you receive the remainder" },
  { v: "dentist_share", l: "You receive the fee — this % is the dentist's share" },
];
export const GST_TREATMENTS = [
  { v: "not_registered", l: "Not registered — no GST" },
  { v: "inclusive", l: "GST-inclusive (GST = amount ÷ 11)" },
  { v: "plus_gst", l: "Plus GST (add 10% on top)" },
  { v: "none", l: "No GST for this practice" },
];
export const DEDUCTION_CATS = [
  { v: "lab", l: "Laboratory fees" },
  { v: "materials", l: "Materials" },
  { v: "other", l: "Other deduction" },
  { v: "custom", l: "Custom / practice-specific" },
];
export const WORK_PATTERNS = [
  { v: "weekly", l: "Regular weekly" },
  { v: "fortnightly", l: "Fortnightly" },
  { v: "monthly", l: "Monthly" },
  { v: "irregular", l: "Irregular / no fixed schedule" },
];

function Field({ label, children, hint }) {
  return (
    <div>
      <Label className="text-slate-700">{label}</Label>
      {hint && <p className="text-xs text-slate-400 mt-0.5">{hint}</p>}
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

function Sel({ value, onChange, options, testId, placeholder }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testId} className="h-12 rounded-xl bg-white"><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent>{options.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}</SelectContent>
    </Select>
  );
}

function Toggle({ value, onChange, testId }) {
  return (
    <div className="flex gap-2" data-testid={testId}>
      <button type="button" data-testid={`${testId}-off`} onClick={() => onChange(false)}
        className={`flex-1 h-11 rounded-xl border text-sm font-medium df-pressable ${!value ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200"}`}>Off</button>
      <button type="button" data-testid={`${testId}-on`} onClick={() => onChange(true)}
        className={`flex-1 h-11 rounded-xl border text-sm font-medium df-pressable ${value ? "bg-[#0D786A] text-white border-[#0D786A]" : "bg-white text-slate-600 border-slate-200"}`}>On — add 10% GST</button>
    </div>
  );
}


export function ArrangementStep({ form, set }) {
  return (
    <div className="space-y-4">
      <Field label="Engagement arrangement">
        <Sel testId="agreement-engagement" value={form.engagement_type} onChange={(v) => set("engagement_type", v)}
          options={ENGAGEMENTS} placeholder="Select arrangement" />
      </Field>
      <p className="text-xs text-slate-500 bg-slate-50 rounded-xl p-3 border border-slate-100">
        Your selection records how you currently understand the arrangement. Molaroo does not determine your legal
        employment or contractor status.
      </p>
    </div>
  );
}

const money = (c) => "$" + (Number(c || 0) / 100).toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function CalcPreview({ form }) {
  const [sample, setSample] = useState({ total: "10000", lab: "500", other: "200" });
  const [snap, setSnap] = useState(null);
  const [err, setErr] = useState("");
  const timer = useRef(null);

  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      const cents = (v) => Math.round((parseFloat(v) || 0) * 100);
      const rule = agreementPayload(form);
      const inputs = {
        billings_cents: cents(sample.total),
        collections_cents: cents(sample.total),
        lab_fees_cents: cents(sample.lab),
        other_deductions_cents: cents(sample.other),
        extra_deductions_cents: 0,
      };
      try {
        const { data } = await api.post("/practices/calc-preview", { rule, inputs });
        setSnap(data); setErr("");
      } catch (e) {
        setErr("Enter the calculation settings above to preview."); setSnap(null);
      }
    }, 350);
    return () => timer.current && clearTimeout(timer.current);
  }, [form, sample]);

  const setS = (k, v) => setSample((s) => ({ ...s, [k]: v }));
  const isAdjusted = form.calc_method === "adjusted_billing";

  return (
    <div className="rounded-2xl border border-emerald-100 bg-[#F4FBF9] p-4 space-y-3" data-testid="calc-preview">
      <div className="text-sm font-semibold text-slate-900">Worked example</div>
      <p className="text-xs text-slate-500 -mt-1">Enter sample figures to confirm Molaroo calculates your arrangement correctly. Nothing is saved.</p>
      <div className="grid grid-cols-3 gap-2">
        <SampleInput label="Total billing" value={sample.total} onChange={(v) => setS("total", v)} testId="preview-total" />
        <SampleInput label="Lab" value={sample.lab} onChange={(v) => setS("lab", v)} testId="preview-lab" />
        <SampleInput label="Other" value={sample.other} onChange={(v) => setS("other", v)} testId="preview-other" />
      </div>
      {err && <div className="text-xs text-amber-600">{err}</div>}
      {snap && (
        <div className="space-y-1.5 text-sm" data-testid="calc-preview-result">
          {isAdjusted && snap.gst_model === "components" && (
            <>
              <Row label="Total billing" value={money(snap.revenue_basis_cents)} />
              <Row label="Less deductions" value={"− " + money(snap.deductions_total_cents)} />
              <Row label="Adjusted billing" value={money(snap.adjusted_billing_cents)} strong />
              <Row label={`Facilities fee (${((snap.service_fee_bps || 0) / 100).toFixed(2)}%)`} value={"− " + money(snap.service_fee_cents)} />
              {snap.service_fee_gst_cents ? <Row label="GST on facilities fee (10%)" value={"− " + money(snap.service_fee_gst_cents)} /> : null}
              <Row label="Dentist amount" value={money(snap.dentist_net_cents)} strong />
              {snap.dentist_gst_cents ? <Row label="Dentist GST (10%)" value={"+ " + money(snap.dentist_gst_cents)} /> : null}
            </>
          )}
          {isAdjusted && snap.gst_model !== "components" && (
            <>
              <Row label="Total billing" value={money(snap.revenue_basis_cents)} />
              <Row label="Less deductions" value={"− " + money(snap.deductions_total_cents)} />
              <Row label="Adjusted billing" value={money(snap.adjusted_billing_cents)} strong />
              <Row label={`Practice / service fee (${((10000 - snap.dentist_share_bps) / 100).toFixed(2)}%)`} value={"− " + money(snap.adjusted_billing_cents - snap.expected_cents)} />
              <Row label={`Dentist receives (${(snap.dentist_share_bps / 100).toFixed(2)}%)`} value={money(snap.expected_cents)} strong />
              {snap.gst_cents ? <Row label="GST" value={money(snap.gst_cents)} /> : null}
            </>
          )}
          {!isAdjusted && (
            <>
              <Row label={snap.revenue_basis_label || "Basis"} value={money(snap.calculation_base_cents ?? snap.revenue_basis_cents)} />
              {snap.percentage_bps != null && <Row label={`Rate ${(snap.percentage_bps / 100).toFixed(2)}%`} value="" />}
            </>
          )}
          <div className="flex items-center justify-between pt-2 mt-1 border-t border-emerald-200">
            <span className="text-sm font-semibold text-slate-900">Expected payment</span>
            <span className="font-num font-bold text-[#0D786A]" data-testid="preview-expected">{money(snap.expected_cents)}</span>
          </div>
          {isAdjusted && snap.total_incl_gst_cents !== snap.expected_cents && (
            <Row label="Total incl. GST" value={money(snap.total_incl_gst_cents)} />
          )}
          {isAdjusted && snap.gst_note && <p className="text-[11px] text-slate-400 pt-1">{snap.gst_note}</p>}
        </div>
      )}
    </div>
  );
}

function Row({ label, value, strong }) {
  return (
    <div className="flex items-center justify-between">
      <span className={`text-slate-600 ${strong ? "font-medium text-slate-900" : ""}`}>{label}</span>
      <span className={`font-num ${strong ? "font-semibold text-slate-900" : "text-slate-700"}`}>{value}</span>
    </div>
  );
}

function SampleInput({ label, value, onChange, testId }) {
  return (
    <div>
      <div className="text-[11px] text-slate-400 mb-1">{label}</div>
      <div className="relative">
        <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 text-sm font-num">$</span>
        <Input inputMode="decimal" data-testid={testId} value={value} onChange={(e) => onChange(e.target.value)}
          className="h-10 rounded-lg bg-white pl-6 font-num text-sm" />
      </div>
    </div>
  );
}

export function CalculationStep({ form, set }) {
  const m = form.calc_method;
  const isPct = ["percentage_billings", "percentage_collections", "percentage_after_lab"].includes(m);
  const isAdjusted = m === "adjusted_billing";
  const toggleDeduction = (k) => {
    const cur = form.deductions || {};
    set("deductions", { ...cur, [k]: !cur[k] });
  };
  return (
    <div className="space-y-4">
      <Field label="Payment calculation method">
        <Sel testId="agreement-method" value={form.calc_method} onChange={(v) => set("calc_method", v)}
          options={METHODS} placeholder="Select method" />
      </Field>

      {isAdjusted && (
        <>
          <Field label="Deduction categories" hint="Choose the deductions taken from total billing before the fee.">
            <div className="flex flex-wrap gap-2">
              {DEDUCTION_CATS.map((d) => {
                const on = !!(form.deductions || {})[d.v];
                return (
                  <button key={d.v} type="button" data-testid={`deduction-${d.v}`} onClick={() => toggleDeduction(d.v)}
                    className={`px-3.5 h-10 rounded-xl border text-sm font-medium df-pressable ${
                      on ? "bg-[#0D786A] text-white border-[#0D786A]" : "bg-white text-slate-600 border-slate-200"}`}>
                    {d.l}
                  </button>
                );
              })}
            </div>
          </Field>
          <Field label="Service / facilities fee (%)">
            <Input type="number" step="0.1" inputMode="decimal" data-testid="agreement-service-fee"
              value={form.service_fee} onChange={(e) => set("service_fee", e.target.value)}
              className="h-12 rounded-xl bg-white font-num" placeholder="e.g. 40" />
          </Field>
          <Field label="How is the fee applied?">
            <Sel testId="agreement-fee-basis" value={form.service_fee_basis} onChange={(v) => set("service_fee_basis", v)}
              options={FEE_BASIS} placeholder="Fee basis" />
          </Field>
          <Field label="GST on Facilities Fee" hint="Adds 10% GST to the practice's calculated facilities/service fee.">
            <Toggle testId="agreement-facilities-gst" value={!!form.service_fee_gst_enabled} onChange={(v) => set("service_fee_gst_enabled", v)} />
          </Field>
          <Field label="Dentist charges GST" hint="Enable where GST applies to the dentist's amount under this practice arrangement.">
            <Toggle testId="agreement-dentist-gst" value={!!form.dentist_gst_enabled} onChange={(v) => set("dentist_gst_enabled", v)} />
          </Field>
          <p className="text-xs text-slate-500 bg-slate-50 rounded-xl p-3 border border-slate-100">
            These two GST settings are independent. Molaroo applies the GST rules you choose — it does not decide whether you must register for or charge GST.
          </p>
        </>
      )}

      {isPct && (
        <Field label="Percentage (%)">
          <Input type="number" step="0.1" inputMode="decimal" data-testid="agreement-percentage"
            value={form.percentage} onChange={(e) => set("percentage", e.target.value)}
            className="h-12 rounded-xl bg-white font-num" placeholder="e.g. 40" />
        </Field>
      )}
      {m === "hourly" && <MoneyField label="Hourly rate" testId="agreement-hourly" value={form.hourly_rate} onChange={(v) => set("hourly_rate", v)} />}
      {m === "daily" && <MoneyField label="Daily rate" testId="agreement-daily" value={form.daily_rate} onChange={(v) => set("daily_rate", v)} />}
      {m === "fixed" && <MoneyField label="Fixed amount per work day" testId="agreement-fixed" value={form.fixed_amount} onChange={(v) => set("fixed_amount", v)} />}
      {m === "custom" && (
        <Field label="Describe the custom arrangement">
          <Textarea data-testid="agreement-custom-notes" value={form.custom_notes}
            onChange={(e) => set("custom_notes", e.target.value)} className="rounded-xl bg-white"
            placeholder="Explain how your payment is calculated. Enter the expected amount per work day when recording work." />
        </Field>
      )}

      {!isAdjusted && (
        <div className="pt-2 border-t border-slate-100">
          <Field label="Lab fees">
            <Sel testId="agreement-lab" value={form.lab_treatment} onChange={(v) => set("lab_treatment", v)}
              options={LAB} placeholder="Lab fee treatment" />
          </Field>
        </div>
      )}
      {form.lab_treatment === "shared" && !isAdjusted && (
        <Field label="Your lab share (%)">
          <Input type="number" step="0.1" inputMode="decimal" data-testid="agreement-lab-share"
            value={form.dentist_lab_share} onChange={(e) => set("dentist_lab_share", e.target.value)}
            className="h-12 rounded-xl bg-white font-num" placeholder="e.g. 50" />
        </Field>
      )}

      {(isAdjusted || isPct) && <CalcPreview form={form} />}
    </div>
  );
}

export function SettingsStep({ form, set }) {
  return (
    <div className="space-y-4">
      <Field label="Payment / reporting frequency" hint="How often this practice reports and pays you.">
        <Sel testId="agreement-frequency" value={form.payment_frequency} onChange={(v) => set("payment_frequency", v)}
          options={FREQS} placeholder="How often are you paid?" />
      </Field>
      <Field label="Super arrangement (as you understand it)">
        <Sel testId="agreement-super" value={form.super_arrangement} onChange={(v) => set("super_arrangement", v)}
          options={SUPERS} placeholder="Super arrangement" />
      </Field>
      <Field label="GST setting">
        <Sel testId="agreement-gst" value={form.gst_setting} onChange={(v) => set("gst_setting", v)}
          options={GSTS} placeholder="GST setting" />
      </Field>
      <Field label="Agreement effective from">
        <Input type="date" data-testid="agreement-effective-start" value={form.effective_start}
          onChange={(e) => set("effective_start", e.target.value)} className="h-12 rounded-xl bg-white" />
      </Field>
      <p className="text-xs text-slate-500">
        These fields record your understanding only. Molaroo does not make super entitlement or GST determinations.
      </p>
    </div>
  );
}

function MoneyField({ label, value, onChange, testId }) {
  return (
    <Field label={label}>
      <div className="relative">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-num">$</span>
        <Input type="number" step="0.01" inputMode="decimal" data-testid={testId} value={value}
          onChange={(e) => onChange(e.target.value)} placeholder="0.00" className="h-12 rounded-xl bg-white pl-7 font-num" />
      </div>
    </Field>
  );
}

export function agreementPayload(form) {
  const bps = (v) => (v === "" || v == null ? null : Math.round(parseFloat(v) * 100));
  const cents = (v) => (v === "" || v == null ? null : Math.round(parseFloat(v) * 100));
  const deductions = form.deductions || {};
  const deduction_categories = Object.keys(deductions).filter((k) => deductions[k]);
  return {
    engagement_type: form.engagement_type,
    calc_method: form.calc_method,
    percentage_bps: bps(form.percentage),
    hourly_rate_cents: cents(form.hourly_rate),
    daily_rate_cents: cents(form.daily_rate),
    fixed_amount_cents: cents(form.fixed_amount),
    custom_notes: form.custom_notes || null,
    lab_treatment: form.calc_method === "adjusted_billing"
      ? ((form.deductions || {}).lab ? "dentist_pays" : "practice_pays")
      : form.lab_treatment,
    dentist_lab_share_bps: form.lab_treatment === "shared" ? bps(form.dentist_lab_share) : null,
    service_fee_bps: bps(form.service_fee),
    service_fee_basis: form.service_fee_basis || "practice_share",
    gst_model: form.calc_method === "adjusted_billing" ? "components" : null,
    service_fee_gst_enabled: !!form.service_fee_gst_enabled,
    dentist_gst_enabled: !!form.dentist_gst_enabled,
    gst_treatment: form.calc_method === "adjusted_billing" ? "components" : (form.gst_treatment || "not_registered"),
    deduction_categories,
    payment_frequency: form.payment_frequency,
    super_arrangement: form.super_arrangement,
    gst_setting: form.gst_setting,
    effective_start: form.effective_start,
  };
}

export const defaultAgreementForm = (start = "2026-07-01") => ({
  engagement_type: "independent_contractor",
  calc_method: "adjusted_billing",
  percentage: "",
  hourly_rate: "",
  daily_rate: "",
  fixed_amount: "",
  custom_notes: "",
  lab_treatment: "practice_pays",
  dentist_lab_share: "",
  service_fee: "",
  service_fee_basis: "practice_share",
  gst_treatment: "not_registered",
  gst_model: "components",
  service_fee_gst_enabled: false,
  dentist_gst_enabled: false,
  deductions: { lab: true, materials: false, other: false, custom: false },
  payment_frequency: "monthly",
  super_arrangement: "unsure",
  gst_setting: "review_required",
  effective_start: start,
});
